/**
 * Aspect — Revive stills gallery
 * --------------------------------------------------------------
 * Stores each Revive capture with its 2D hitboxes so a still can be
 * reopened as the same interactive price-hover image.
 */

const MAX_ITEMS = 16;

function dataUrlToBlob(src) {
  const raw = String(src);
  const marker = ";base64,";
  const idx = raw.indexOf(marker);
  if (!raw.startsWith("data:") || idx < 0) return null;
  const mime = raw.slice(5, idx) || "image/jpeg";
  const binary = atob(raw.slice(idx + marker.length));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function filenameFor(item, mime = "image/jpeg") {
  const ext = mime.includes("png") ? "png" : mime.includes("webp") ? "webp" : "jpg";
  const slug =
    String(item?.title || "view")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "view";
  const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
  return `aspect-revive-${slug}-${stamp}.${ext}`;
}

function cloneState(state) {
  if (!state) return null;
  try {
    return structuredClone(state);
  } catch {
    return JSON.parse(JSON.stringify(state));
  }
}

export class ReviveGallery {
  /**
   * @param {{ onOpen?: (item: object) => void }} [callbacks]
   */
  constructor(callbacks = {}) {
    this.onOpen = callbacks.onOpen ?? (() => {});
    this.onPhotoreal = callbacks.onPhotoreal ?? (() => {});
    /** @type {{ id: string, src: string, title: string, state: object|null }[]} */
    this.items = [];
    this._index = 0;
    /** @type {HTMLElement|null} */
    this.root = null;
    this._esc = null;
    this._dlUrl = "";
  }

  /**
   * Add a Revive capture. `state` is the camera + hitboxes snapshot.
   * Skips an exact duplicate of the most recent still.
   */
  add({ src, title = "Captured view", state = null } = {}) {
    if (!src) return null;
    const last = this.items[0];
    if (last && last.src === src) {
      if (state && !last.state) last.state = cloneState(state);
      this._index = 0;
      if (this.root) this._render();
      return last;
    }
    const item = {
      id: `rv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      src,
      title,
      photoreal: false,
      state: cloneState(state),
    };
    this.items.unshift(item);
    if (this.items.length > MAX_ITEMS) this.items.length = MAX_ITEMS;
    this._index = 0;
    if (this.root) this._render();
    return item;
  }

  removeAt(index) {
    if (!this.items[index]) return;
    this.items.splice(index, 1);
    if (this._index >= this.items.length) this._index = Math.max(0, this.items.length - 1);
    if (this.root) this._render();
  }

  /** Write back hitboxes after an outline is removed on the open still. */
  updateState(src, state) {
    if (!src || !state) return null;
    const item = this.items.find((it) => it.src === src);
    if (!item) return null;
    item.state = cloneState(state);
    if (this.root) this._render();
    return item;
  }

  /** Swap the still only — hitboxes stay on the same item. */
  replaceSrc(indexOrSrc, src, { title, photoreal } = {}) {
    if (!src) return null;
    const item =
      typeof indexOrSrc === "number"
        ? this.items[indexOrSrc]
        : this.items.find((it) => it.src === indexOrSrc);
    if (!item) return null;
    item.src = src;
    if (title) item.title = title;
    if (photoreal != null) item.photoreal = Boolean(photoreal);
    if (this.root) this._render();
    return item;
  }

  setBusy(busy) {
    if (!this.root) return;
    const on = Boolean(busy);
    const btn = this.root.querySelector('[data-gallery="photoreal"]');
    if (btn) btn.disabled = on;
    this.root.classList.toggle("revive-gallery--busy", on);
    const wait = this.root.querySelector('[data-gallery="wait"]');
    if (wait) {
      if (on) {
        wait.hidden = false;
        requestAnimationFrame(() => wait.classList.add("revive-layer__wait--on"));
      } else {
        wait.classList.remove("revive-layer__wait--on");
        window.setTimeout(() => {
          wait.hidden = true;
          this._render();
        }, 720);
        return;
      }
    }
    if (!on && this.root) this._render();
  }

  toggle() {
    if (this.root) this.close();
    else this.open();
  }

  open() {
    if (this.root) return;
    const root = document.createElement("div");
    root.className = "revive-gallery";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-label", "Revived images");
    root.innerHTML = `
      <header class="revive-gallery__bar">
        <div class="revive-gallery__title">
          <span class="revive-gallery__kicker">Revive</span>
          <h2>Revived Images</h2>
        </div>
        <div class="revive-gallery__actions">
          <button type="button" class="hud-btn hud-btn--ghost" data-gallery="photoreal">
            Make photoreal
          </button>
          <button type="button" class="hud-btn hud-btn--ghost" data-gallery="replace">
            Replace still
          </button>
          <a class="hud-btn hud-btn--ghost" data-gallery="download" download="aspect-revive.jpg">
            Download image
          </a>
          <button type="button" class="hud-btn hud-btn--ghost" data-gallery="delete">
            Delete
          </button>
          <button type="button" class="hud-btn hud-btn--primary" data-gallery="open">
            Open interactive
          </button>
          <button type="button" class="hud-btn hud-btn--ghost" data-gallery="close">
            Close
          </button>
        </div>
      </header>
      <div class="revive-gallery__stage">
        <button type="button" class="revive-gallery__nav revive-gallery__nav--prev" data-gallery="prev" aria-label="Previous image">‹</button>
        <div class="revive-gallery__frame" data-gallery="frame"></div>
        <button type="button" class="revive-gallery__nav revive-gallery__nav--next" data-gallery="next" aria-label="Next image">›</button>
        <div class="revive-gallery__wait" data-gallery="wait" hidden>
          <div class="revive-layer__orb" aria-hidden="true">
            <span class="revive-layer__ring revive-layer__ring--outer"></span>
            <span class="revive-layer__ring revive-layer__ring--inner"></span>
            <span class="revive-layer__core"></span>
          </div>
          <p class="revive-layer__wait-kicker">Aspect Revive</p>
          <p class="revive-layer__wait-title">Letting the light in</p>
          <p class="revive-layer__wait-sub">Furniture stays. Shadows become real.</p>
        </div>
      </div>
      <div class="revive-gallery__film" data-gallery="film"></div>
      <input type="file" accept="image/*" hidden data-gallery="file" />
    `;
    document.body.appendChild(root);
    document.body.classList.add("revive-gallery-open");
    this.root = root;

    root.querySelector('[data-gallery="close"]').addEventListener("click", () => this.close());
    root.querySelector('[data-gallery="open"]').addEventListener("click", () => this._openCurrent());
    root.querySelector('[data-gallery="photoreal"]').addEventListener("click", () => {
      const item = this.items[this._index];
      if (!item) return;
      this.onPhotoreal(item, this._index);
    });
    root.querySelector('[data-gallery="download"]').addEventListener("click", (e) => {
      e.stopPropagation();
    });
    root.querySelector('[data-gallery="delete"]').addEventListener("click", () => this.removeAt(this._index));
    root.querySelector('[data-gallery="replace"]').addEventListener("click", () => {
      if (!this.items[this._index]) return;
      root.querySelector('[data-gallery="file"]')?.click();
    });
    root.querySelector('[data-gallery="file"]').addEventListener("change", (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      const item = this.items[this._index];
      if (!file || !item) return;
      const reader = new FileReader();
      reader.onload = () => {
        item.src = String(reader.result || "");
        item.title = file.name.replace(/\.[^.]+$/, "") || item.title;
        this._render();
      };
      reader.readAsDataURL(file);
    });
    root.querySelector('[data-gallery="prev"]').addEventListener("click", () => this._step(-1));
    root.querySelector('[data-gallery="next"]').addEventListener("click", () => this._step(1));

    this._esc = (e) => {
      if (e.key === "Escape") this.close();
      else if (e.key === "ArrowLeft") this._step(-1);
      else if (e.key === "ArrowRight") this._step(1);
      else if (e.key === "Enter") this._openCurrent();
    };
    window.addEventListener("keydown", this._esc);
    this._render();
  }

  close() {
    if (this._esc) window.removeEventListener("keydown", this._esc);
    this._esc = null;
    this.root?.remove();
    this.root = null;
    this._revokeDownload();
    document.body.classList.remove("revive-gallery-open");
  }

  _revokeDownload() {
    if (this._dlUrl) URL.revokeObjectURL(this._dlUrl);
    this._dlUrl = "";
  }

  _setChosenDownload(item) {
    const links = [
      this.root?.querySelector('[data-gallery="download"]'),
      this.root?.querySelector(".revive-gallery__shot-dl"),
    ].filter(Boolean);
    this._revokeDownload();
    if (!item?.src || !links.length) return;
    const blob = dataUrlToBlob(item.src);
    const href = blob ? (this._dlUrl = URL.createObjectURL(blob)) : item.src;
    const filename = filenameFor(item, blob?.type);
    for (const el of links) {
      el.href = href;
      el.download = filename;
    }
  }

  _openCurrent() {
    const item = this.items[this._index];
    if (!item?.state) return;
    this.close();
    this.onOpen(item);
  }

  _step(dir) {
    if (!this.items.length) return;
    this._index = (this._index + dir + this.items.length) % this.items.length;
    this._render();
  }

  _pick(index) {
    if (!this.items[index]) return;
    this._index = index;
    this._render();
  }

  _render() {
    const root = this.root;
    if (!root) return;
    const frame = root.querySelector('[data-gallery="frame"]');
    const film = root.querySelector('[data-gallery="film"]');
    const item = this.items[this._index];
    const has = this.items.length > 0;

    root.querySelector('[data-gallery="prev"]').hidden = !has || this.items.length < 2;
    root.querySelector('[data-gallery="next"]').hidden = !has || this.items.length < 2;
    root.querySelector('[data-gallery="delete"]').hidden = !has;
    root.querySelector('[data-gallery="replace"]').hidden = !has;
    root.querySelector('[data-gallery="photoreal"]').hidden = !has;
    root.querySelector('[data-gallery="download"]').hidden = !has;
    root.querySelector('[data-gallery="open"]').hidden = !has || !item?.state;

    if (!item) {
      frame.innerHTML = `
        <div class="revive-gallery__empty">
          <p>No revived images yet.</p>
          <p>Click <strong>Revive</strong> to save this view with item positions. Open a still from here to hover prices again.</p>
        </div>`;
      film.innerHTML = "";
      return;
    }

    frame.innerHTML = "";
    const img = document.createElement("img");
    img.className = "revive-gallery__img";
    img.src = item.src;
    img.alt = item.title;
    img.draggable = false;
    img.title = "Open as interactive Revive";
    img.addEventListener("click", () => this._openCurrent());
    frame.appendChild(img);
    const onImageDl = document.createElement("a");
    onImageDl.className = "revive-gallery__shot-dl";
    onImageDl.textContent = "Download image";
    onImageDl.rel = "noopener";
    onImageDl.addEventListener("click", (e) => e.stopPropagation());
    frame.appendChild(onImageDl);
    this._setChosenDownload(item);
    const cap = document.createElement("p");
    cap.className = "revive-gallery__caption";
    const n = item.state?.hitboxes?.length ?? 0;
    cap.textContent = `${this._index + 1} / ${this.items.length}  ·  ${item.title}  ·  ${n} items`;
    frame.appendChild(cap);

    film.innerHTML = "";
    this.items.forEach((it, i) => {
      const wrap = document.createElement("div");
      wrap.className = "revive-gallery__shot";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "revive-gallery__thumb" + (i === this._index ? " revive-gallery__thumb--on" : "");
      btn.setAttribute("aria-label", it.title);
      const thumb = document.createElement("img");
      thumb.src = it.src;
      thumb.alt = "";
      thumb.draggable = false;
      btn.appendChild(thumb);
      btn.addEventListener("click", () => this._pick(i));
      btn.addEventListener("dblclick", () => {
        this._index = i;
        this._openCurrent();
      });
      const del = document.createElement("button");
      del.type = "button";
      del.className = "revive-gallery__thumb-del";
      del.setAttribute("aria-label", "Delete image");
      del.textContent = "×";
      del.addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeAt(i);
      });
      wrap.appendChild(btn);
      wrap.appendChild(del);
      film.appendChild(wrap);
    });
  }
}

export default ReviveGallery;
